import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight,
  ArrowRight,
  Leaf,
  Sparkles,
  ShieldCheck,
  Wrench,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  Quote,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ProductsPage from "@/pages/ProductsPage";
import ClassinaPage from "@/pages/ClassinaPage";
import { FEATURED_PRODUCTS } from "@/data/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/73beafd6-f905-4c03-a95d-f6ba4441dadf/images/b0f299594a4f7578da5abb1692e0995c9df51ca00bffe1c7fb8cd08b0eb2b1af.png",
  paperBg:
    "https://static.prod-images.emergentagent.com/jobs/73beafd6-f905-4c03-a95d-f6ba4441dadf/images/0c356d107f435b33125212a138d30a2d80846280593738d34e4b77d59b404e59.png",
  process:
    "https://static.prod-images.emergentagent.com/jobs/73beafd6-f905-4c03-a95d-f6ba4441dadf/images/05301de02ef50c9dec4f79cd411d5dd37191cae1b75f40c6220d6f8e4ebd90b3.png",
  journal:
    "https://static.prod-images.emergentagent.com/jobs/73beafd6-f905-4c03-a95d-f6ba4441dadf/images/20f01275d9009d20872f083faeacd1b93db30ff4f9d8e22954a08921c3f86797.png",
};

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-screen w-full overflow-hidden"
  >
    <img
      src={IMG.hero}
      alt="Premium notebooks stacked"
      className="absolute inset-0 w-full h-full object-cover opacity-60"
    />
    <div className="absolute inset-0 bg-ink-900/30" />
    <div className="absolute inset-0 bg-gradient-to-b from-ink-900/15 via-transparent to-ink-900/55" />

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-screen flex flex-col justify-between">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8 reveal">
          <span className="w-10 h-px bg-stone-50/60" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-50/80">
            Pune · India · Est. 2014
          </span>
        </div>
        <h1
          data-testid="hero-title"
          className="font-serif font-light text-stone-50 text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight reveal"
        >
          Notebooks, made to{" "}
          <span className="italic text-stone-50">last a lifetime</span> — and
          travel the world.
        </h1>
        <p className="mt-8 max-w-2xl text-stone-50/85 text-lg md:text-xl leading-relaxed font-light reveal">
          SBK Manufacturing Company crafts premium, customizable notebooks for
          students, professionals and creatives — exported across continents,
          benchmarked on quality, paper after paper.
        </p>

        <div className="mt-10 flex flex-wrap gap-4 reveal">
          <a
            href="#products"
            data-testid="hero-cta-products"
            className="inline-flex items-center gap-2 bg-stone-50 text-ink-900 px-7 py-4 rounded-sm font-medium hover:bg-stone-100 transition-colors"
          >
            Explore Products <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="inline-flex items-center gap-2 border border-stone-50/60 text-stone-50 px-7 py-4 rounded-sm font-medium hover:bg-stone-50 hover:text-ink-900 transition-all"
          >
            Request a Custom Quote <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="hidden md:grid grid-cols-4 gap-10 mt-16 pt-10 border-t border-stone-50/15">
        {[
          { k: "10+", l: "Years of craftsmanship" },
          { k: "20+", l: "Export destinations" },
          { k: "10M+", l: "Notebooks delivered annually" },
          { k: "100%", l: "Customizable, end to end" },
        ].map((s) => (
          <div key={s.k} data-testid={`hero-stat-${s.k}`} className="reveal">
            <div className="font-serif text-4xl text-stone-50">{s.k}</div>
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-50/70 mt-2">
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="relative py-24 md:py-32 paper-grain"
    style={{
      backgroundImage: `url(${IMG.paperBg})`,
      backgroundSize: "cover",
      backgroundBlendMode: "multiply",
      backgroundColor: "#F6F4EF",
    }}
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-5 reveal">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-ink-900/40" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
            About SBK
          </span>
        </div>
        <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
          Quality you can <em className="text-terracotta-600 not-italic">feel</em>.
          Craft you can <em className="italic font-normal">trust</em>.
        </h2>
        <div className="mt-8 aspect-[4/5] overflow-hidden rounded-sm border border-stone-200">
          <img
            src={IMG.journal}
            alt="Open journal notebook"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
      </div>

      <div className="lg:col-span-7 lg:pt-20 reveal">
        <p className="text-lg md:text-xl leading-relaxed text-stone-800 font-light">
          SBK Manufacturing Company is dedicated to producing world-class
          notebooks that set benchmarks in quality and craftsmanship. As a
          trusted exporter, we take immense pride in delivering products that
          consistently meet and exceed global standards.
        </p>
        <p className="mt-6 text-base md:text-lg leading-relaxed text-stone-700">
          Our commitment lies in understanding the evolving needs of our
          customers and transforming them into thoughtfully designed notebooks.
          With a strong focus on customization, superior materials and precision
          manufacturing, our products cater seamlessly to students, professionals
          and creative users across international markets.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {[
            "Premium FSC-grade paper sourcing",
            "Hardcover, softcover & spiral binding",
            "Custom debossing, foiling & printing",
            "Bulk export-ready packaging",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-terracotta-600 flex-shrink-0 mt-0.5" />
              <span className="text-ink-900">{t}</span>
            </div>
          ))}
        </div>

        <a
          href="#contact"
          data-testid="about-cta"
          className="mt-12 inline-flex items-center gap-2 text-ink-900 font-medium border-b border-ink-900 pb-1 hover:gap-4 transition-all"
        >
          Talk to our export team <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

const Products = () => (
  <section
    id="products"
    data-testid="products-section"
    className="bg-stone-50 py-24 md:py-32"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 reveal">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-ink-900/40" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
              What We Make
            </span>
          </div>
          <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
            A notebook for every hand,{" "}
            <span className="italic">every desk</span>, every market.
          </h2>
          <p className="mt-5 text-stone-700 max-w-xl leading-relaxed">
            From classroom essentials to corporate journals, every product is manufactured
            in our Pune facility — customizable end to end, export-ready.
          </p>
        </div>
        <Link
          to="/products"
          data-testid="products-view-all"
          className="inline-flex items-center gap-2 bg-ink-900 text-stone-50 px-7 py-3.5 rounded-sm font-medium hover:bg-ink-800 hover:gap-4 transition-all w-max shadow-md hover:shadow-lg"
        >
          View full catalogue <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {FEATURED_PRODUCTS.map((p, i) => (
          <article
            key={p.slug}
            data-testid={`product-card-${p.slug}`}
            className="group bg-white border border-stone-200 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-ink-900/5 transition-all duration-500 flex flex-col reveal"
          >
            <div className="aspect-square overflow-hidden bg-white p-6 flex items-center justify-center">
              <img
                src={p.image}
                alt={p.name}
                loading={i < 3 ? "eager" : "lazy"}
                className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="p-7 md:p-8 flex-1 flex flex-col">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-terracotta-600 mb-3">
                {p.category}
              </span>
              <h3 className="font-serif text-2xl md:text-3xl text-ink-900 leading-tight">
                {p.name}
              </h3>
              <p className="mt-3 text-stone-700 leading-relaxed text-sm flex-1">
                {p.description}
              </p>
              <a
                href={`#contact?inquiry=${encodeURIComponent(p.name)}`}
                data-testid={`product-inquire-${p.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  const url = new URL(window.location.href);
                  url.searchParams.set("inquiry", p.name);
                  url.hash = "contact";
                  window.history.replaceState({}, "", url.toString());
                  window.dispatchEvent(new CustomEvent("sbk-prefill-contact", { detail: p.name }));
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-6 inline-flex items-center gap-2 text-ink-900 text-sm font-medium border-b border-ink-900 pb-1 self-start hover:gap-4 transition-all"
              >
                Inquire <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Classina = () => (
  <section
    id="classina"
    data-testid="classina-section"
    className="relative py-24 md:py-32 paper-grain"
    style={{
      backgroundImage: `url(${IMG.paperBg})`,
      backgroundSize: "cover",
      backgroundBlendMode: "multiply",
      backgroundColor: "#F6F4EF",
    }}
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7 reveal">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-ink-900/40" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
            Our House Brand
          </span>
        </div>
        <img
          src="/classina-logo.png"
          alt="Classina Products"
          className="h-14 md:h-20 w-auto mb-8 select-none"
          draggable="false"
        />
        <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
          We don&rsquo;t just <em className="italic">manufacture</em> — we{" "}
          <span className="text-terracotta-600">brand</span>.
        </h2>
        <p className="mt-6 text-lg text-stone-800 leading-relaxed font-light max-w-2xl">
          Beyond making notebooks for global partners, we design and ship our
          own line of stationery under <strong>Classina®</strong> — a
          registered brand built on the same craftsmanship that defines SBK.
          From vibrant A4 softbound notebooks for every classroom to our newest
          sustainable Eco Series, printed on recyclable kraft, Classina carries
          our story straight to the desk.
        </p>
        <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-ink-900 text-stone-50 rounded-sm">
          <Leaf className="w-4 h-4 text-terracotta-500" />
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
            Write Green, Live Clean — the Eco Series
          </span>
        </div>
        <div className="mt-10">
          <a
            href="/classina"
            data-testid="classina-cta"
            className="inline-flex items-center gap-2 text-ink-900 font-medium border-b border-ink-900 pb-1 hover:gap-4 transition-all"
          >
            Browse Classina range <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
      <div className="lg:col-span-5 reveal">
        <div className="aspect-square overflow-hidden rounded-sm border border-stone-200 bg-white">
          <img
            src="https://customer-assets.emergentagent.com/job_revamp-2024/artifacts/0izh8zwp_8908030416025_f.png"
            alt="Classina Eco Series A4 notebook"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
        <div className="mt-4 flex items-center gap-3 text-stone-700">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-terracotta-600">
            New Launch
          </span>
          <span className="text-sm">Classina Eco Series · A4 Ruled · 160 Pages</span>
        </div>
      </div>
    </div>
  </section>
);

const Capabilities = () => (
  <section
    id="capabilities"
    data-testid="capabilities-section"
    className="bg-ink-900 text-stone-50 py-24 md:py-32 relative overflow-hidden"
  >
    <div className="absolute inset-0 opacity-[0.04]">
      <img src={IMG.paperBg} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-6 reveal">
        <div className="aspect-[4/3] overflow-hidden rounded-sm">
          <img
            src={IMG.process}
            alt="Manufacturing facility"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
      </div>
      <div className="lg:col-span-6 reveal">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-stone-50/40" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-50/70">
            Manufacturing Capabilities
          </span>
        </div>
        <h2 className="font-serif font-light text-stone-50 text-4xl md:text-5xl leading-tight tracking-tight">
          Modern machines.{" "}
          <span className="italic">Old-world attention</span> to detail.
        </h2>
        <p className="mt-6 text-stone-50/80 text-lg leading-relaxed font-light">
          Our integrated facility in Pune handles every step under one roof —
          paper cutting, printing, binding and finishing — so quality is never
          handed off, only handed forward.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
          {[
            { k: "Paper", v: "70 – 120 GSM, FSC certified" },
            { k: "Print", v: "Offset, digital, foil & deboss" },
            { k: "Binding", v: "Casebound, perfect, spiral, stitch" },
            { k: "Capacity", v: "10M+ units / year" },
          ].map((c) => (
            <div key={c.k} className="border-t border-stone-50/20 pt-4">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-50/60">
                {c.k}
              </div>
              <div className="font-serif text-xl text-stone-50 mt-2">{c.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const WhyChoose = () => {
  const pillars = [
    { icon: ShieldCheck, title: "Quality Assurance", desc: "Sourced from finest mills, audited at every stage. Built to outlast trends and travel.", span: "lg:col-span-5" },
    { icon: Wrench, title: "Customization", desc: "Cover, paper, binding, foiling, ruling — every notebook can be tailored end to end.", span: "lg:col-span-7" },
    { icon: Leaf, title: "Sustainability", desc: "Eco-friendly papers, responsible sourcing and low-impact production processes.", span: "lg:col-span-7" },
    { icon: Sparkles, title: "Innovation", desc: "Cutting-edge designs, finishes and printing capabilities — refreshed with every season.", span: "lg:col-span-5" },
  ];

  return (
    <section id="why" data-testid="why-section" className="bg-stone-50 py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-ink-900/40" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
              Why choose SBK
            </span>
          </div>
          <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
            Four pillars. <span className="italic">One promise</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className={`${p.span} bg-white border border-stone-200 rounded-sm p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl hover:shadow-ink-900/5 transition-all duration-500 reveal`}
              >
                <div className="w-12 h-12 rounded-sm bg-ink-900 text-stone-50 grid place-items-center mb-8">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-3xl text-ink-900 leading-tight">{p.title}</h3>
                <p className="mt-4 text-stone-700 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ExportRibbon = () => {
  const places = [
    "United States", "United Kingdom", "Germany", "France", "United Arab Emirates",
    "Singapore", "Australia", "Japan", "South Africa", "Canada",
  ];
  const row = [...places, ...places];
  return (
    <section data-testid="export-ribbon" className="bg-stone-100 border-y border-stone-200 py-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-px bg-ink-900/40" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
            Trusted & exported across
          </span>
        </div>
      </div>
      <div className="marquee-track items-center">
        {row.map((p, i) => (
          <div key={i} className="flex items-center px-8 md:px-12 shrink-0">
            <span className="font-serif text-3xl md:text-5xl text-ink-900/80 italic">{p}</span>
            <span className="ml-8 md:ml-12 w-2 h-2 rounded-full bg-terracotta-600" />
          </div>
        ))}
      </div>
    </section>
  );
};

const Testimonial = () => (
  <section data-testid="testimonial-section" className="bg-stone-50 py-24 md:py-32">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center reveal">
      <Quote className="w-10 h-10 text-terracotta-600 mx-auto mb-8" />
      <p className="font-serif text-ink-900 text-3xl md:text-5xl leading-tight font-light">
        &ldquo;SBK has been our go-to manufacturing partner for premium journals
        across three continents. Their consistency, paper quality and
        willingness to customize is unmatched.&rdquo;
      </p>
      <div className="mt-10 flex flex-col items-center gap-2">
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
          European Stationery Importer
        </span>
        <span className="text-ink-900 font-medium">— Long-term Client</span>
      </div>
    </div>
  </section>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: null, err: "" });
  const location = useLocation();

  // Pre-fill subject from URL ?inquiry= or custom event.
  // Intentionally syncing external URL state into form state.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inquiry = params.get("inquiry");
    if (inquiry) {
      setForm((f) => ({ ...f, subject: `Inquiry — ${inquiry}` }));
    }
  }, [location.search]);

  useEffect(() => {
    const handler = (e) => {
      const name = e?.detail;
      if (name) setForm((f) => ({ ...f, subject: `Inquiry — ${name}` }));
    };
    window.addEventListener("sbk-prefill-contact", handler);
    return () => window.removeEventListener("sbk-prefill-contact", handler);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: null, err: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setStatus({ loading: false, ok: false, err: String(msg) });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink-900 text-stone-50 py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]">
        <img src={IMG.paperBg} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-stone-50/40" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-50/70">
              Let&rsquo;s chat
            </span>
          </div>
          <h2 className="font-serif font-light text-stone-50 text-4xl md:text-5xl leading-tight tracking-tight">
            Say <em className="italic text-terracotta-500">hello</em> — we ship
            samples globally.
          </h2>
          <p className="mt-6 text-stone-50/80 leading-relaxed font-light">
            Whether you need 500 corporate journals or 50,000 export-ready
            notebooks, our team will respond within one business day.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-terracotta-500 mt-1" />
              <div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-50/60 mb-1">
                  Registered Office
                </div>
                <div className="text-stone-50">
                  230/4 Nana Peth, Pragati Villa, Pune 411002, India
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=230%2C%204%2C%20New%20Nana%20Peth%2C%20Rasta%20Peth%2C%20Pune%2C%20Maharashtra%20411002%2C%20India"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions"
                  className="inline-flex items-center gap-1.5 mt-2 text-terracotta-500 text-sm hover:underline"
                >
                  Get directions <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-terracotta-500 mt-1" />
              <div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-50/60 mb-1">
                  Phone
                </div>
                <a href="tel:+919960749074" className="block text-stone-50 hover:text-terracotta-500 transition-colors">+91 99607 49074</a>
                <a href="tel:+919175444440" className="block text-stone-50 hover:text-terracotta-500 transition-colors">+91 91754 44440</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="w-5 h-5 text-terracotta-500 mt-1" />
              <div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-50/60 mb-1">
                  Email
                </div>
                <a href="mailto:info@sbkmanufacturing.com" className="text-stone-50 hover:text-terracotta-500 transition-colors">
                  info@sbkmanufacturing.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-7 bg-stone-50 text-ink-900 rounded-sm p-8 md:p-12 reveal">
          <h3 className="font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
            Start a conversation
          </h3>
          <p className="text-stone-700 mt-2">
            Tell us about your project — we&rsquo;ll reply within 1 business day.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-700">Your Name</label>
              <input required name="name" value={form.name} onChange={onChange} data-testid="contact-input-name" className="mt-2 w-full bg-transparent border-b border-ink-900/30 py-3 text-ink-900 outline-none focus:border-ink-900 transition-colors" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-700">Email</label>
              <input required type="email" name="email" value={form.email} onChange={onChange} data-testid="contact-input-email" className="mt-2 w-full bg-transparent border-b border-ink-900/30 py-3 text-ink-900 outline-none focus:border-ink-900 transition-colors" placeholder="you@company.com" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-700">Subject</label>
              <input required name="subject" value={form.subject} onChange={onChange} data-testid="contact-input-subject" className="mt-2 w-full bg-transparent border-b border-ink-900/30 py-3 text-ink-900 outline-none focus:border-ink-900 transition-colors" placeholder="Bulk order inquiry" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-700">Message</label>
              <textarea required name="message" value={form.message} onChange={onChange} rows={5} data-testid="contact-input-message" className="mt-2 w-full bg-transparent border-b border-ink-900/30 py-3 text-ink-900 outline-none focus:border-ink-900 transition-colors resize-none" placeholder="Quantity, customization, delivery destination…" />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <button type="submit" disabled={status.loading} data-testid="contact-submit" className="inline-flex items-center justify-center gap-2 bg-ink-900 text-stone-50 px-8 py-4 rounded-sm font-medium hover:bg-ink-800 transition-colors disabled:opacity-60">
              {status.loading ? "Sending..." : "Send Message"}
              <ArrowUpRight className="w-4 h-4" />
            </button>
            {status.ok === true && (
              <span data-testid="contact-success" className="text-ink-900 font-medium">
                Thanks for submitting! We&rsquo;ll be in touch.
              </span>
            )}
            {status.ok === false && (
              <span data-testid="contact-error" className="text-terracotta-700">{status.err}</span>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

const Home = () => {
  useReveal();
  const location = useLocation();

  // Smooth-scroll to hash on initial mount or when navigating to /#section
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    document.title = "SBK Manufacturing Co. · Premium Notebook Manufacturer & Exporter";
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <Hero />
      <About />
      <Products />
      <Capabilities />
      <Classina />
      <ExportRibbon />
      <WhyChoose />
      <TradeShows />
      <Testimonial />
      <Contact />
      <Footer />
    </div>
  );
};

const TradeShows = () => {
  const shows = [
    {
      key: "london-stationery-show",
      name: "London International Stationery Show",
      year: "2026",
      venue: "Business Design Centre · Islington, London",
      blurb:
        "Our debut at LISS — presented the full Classina® house brand and SBK export catalogue at Stand G510, hosting UK and European retailers, OEM partners and global distributors.",
      accent: "#C45B3A",
      images: [
        "/uploads/trade-shows/liss-1.jpg",
        "/uploads/trade-shows/liss-2.jpg",
        "/uploads/trade-shows/liss-7.jpg",
        "/uploads/trade-shows/liss-3.jpg",
        "/uploads/trade-shows/liss-4.jpg",
        "/uploads/trade-shows/liss-5.jpg",
        "/uploads/trade-shows/liss-6.jpg",
      ],
    },
    {
      key: "paperworld-mumbai",
      name: "Paperworld Mumbai",
      year: "2024",
      venue: "Bombay Exhibition Centre",
      blurb:
        "Launched our premium Classina line and showcased our full export catalogue — hosting distributors, retailers and OEM buyers from across India and South Asia.",
      accent: "#C45B3A",
      images: [
        "/uploads/trade-shows/paperworld-1.jpg",
        "/uploads/trade-shows/paperworld-6.jpg",
        "/uploads/trade-shows/paperworld-5.jpg",
        "/uploads/trade-shows/paperworld-7.jpg",
        "/uploads/trade-shows/paperworld-2.jpg",
        "/uploads/trade-shows/paperworld-3.jpg",
        "/uploads/trade-shows/paperworld-4.jpg",
      ],
    },
    {
      key: "kenya-trade-expo",
      name: "Kenya Trade Expo",
      year: "2024",
      venue: "Nairobi, Kenya",
      blurb:
        "Exhibited at our Indian pavilion (Booth B2) — introduced Classina Eco Series and connected with East African distributors across stationery, school supply and corporate gifting verticals.",
      accent: "#D4AF37",
      images: [
        "/uploads/trade-shows/kenya-1.jpg",
        "/uploads/trade-shows/kenya-2.jpg",
        "/uploads/trade-shows/kenya-3.jpg",
        "/uploads/trade-shows/kenya-4.jpg",
        "/uploads/trade-shows/kenya-5.jpg",
        "/uploads/trade-shows/kenya-6.jpg",
        "/uploads/trade-shows/kenya-7.jpg",
        "/uploads/trade-shows/kenya-8.jpg",
      ],
    },
  ];

  return (
    <section
      id="exhibitions"
      data-testid="trade-shows-section"
      className="py-20 md:py-28 bg-stone-100 border-y border-stone-200 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-ink-900/40" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
              Trade shows &amp; Exhibitions
            </span>
          </div>
          <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
            Where <em className="italic">the world</em> meets SBK.
          </h2>
          <p className="mt-5 text-stone-700 leading-relaxed">
            From Mumbai to Nairobi — we show up, shake hands and turn displays
            into distribution. A few moments from the road.
          </p>
        </div>

        <div className="space-y-14 md:space-y-20">
          {shows.map((s, si) => (
            <div
              key={s.key}
              data-testid={`trade-show-${s.key}`}
              className="reveal"
            >
              <div className="flex items-end justify-between gap-6 mb-6 pb-5 border-b border-stone-300">
                <div className="flex items-end gap-5">
                  <span
                    className="font-mono text-[12px] tracking-[0.3em] uppercase leading-none"
                    style={{ color: s.accent }}
                  >
                    0{si + 1}
                  </span>
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-700 block mb-1.5">
                      {s.venue} · {s.year}
                    </span>
                    <h3 className="font-serif text-ink-900 text-2xl md:text-3xl leading-tight">
                      {s.name}
                    </h3>
                  </div>
                </div>
                <p className="hidden md:block text-stone-700 text-sm max-w-md leading-relaxed">
                  {s.blurb}
                </p>
              </div>
              <p className="md:hidden mb-5 text-stone-700 text-sm leading-relaxed">
                {s.blurb}
              </p>

              {s.comingSoon ? (
                <div
                  data-testid={`trade-show-${s.key}-coming-soon`}
                  className="border border-dashed border-stone-400 bg-white/60 rounded-sm py-12 md:py-16 px-6 flex flex-col items-center text-center"
                  style={{ borderColor: s.accent + "55" }}
                >
                  <span
                    className="font-mono text-[10px] tracking-[0.3em] uppercase mb-3"
                    style={{ color: s.accent }}
                  >
                    Photo gallery
                  </span>
                  <p className="font-serif italic text-ink-900 text-xl md:text-2xl leading-tight max-w-xl">
                    Photos coming soon — we’re still in London.
                  </p>
                  <p className="mt-3 text-stone-600 text-sm max-w-md">
                    Check back after 13 May 2026 for highlights from the show floor at Stand G510.
                  </p>
                </div>
              ) : (
                <div className="flex gap-4 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 md:-mx-10 px-6 md:px-10">
                  {s.images.map((src, i) => (
                    <div
                      key={i}
                      className="snap-start flex-shrink-0 relative bg-white p-3 md:p-4 pb-8 md:pb-10 rounded-sm shadow-md hover:shadow-xl hover:-translate-y-1 hover:rotate-0 transition-all duration-500"
                      style={{
                        width: "280px",
                        transform: `rotate(${[-1.2, 0.8, -0.5, 1.1, -0.9][i % 5]}deg)`,
                      }}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={src}
                          alt={`${s.name} — photo ${i + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute left-0 right-0 bottom-2 text-center font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600">
                        {s.name} · {s.year}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/classina" element={<ClassinaPage />} />
        </Routes>
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    const seenKey = `sbk_visit_${path}`;
    if (sessionStorage.getItem(seenKey)) return;
    sessionStorage.setItem(seenKey, "1");
    axios
      .post(`${API}/track-visit`, {
        page: path,
        title: document.title,
        referrer: document.referrer || "",
        user_agent: navigator.userAgent,
      })
      .catch(() => {});
  }, [location.pathname, location.search]);
  return null;
};

export default App;
